import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { UserCog, Lock, ArrowLeft, Building2, Loader2, Shield, AlertTriangle, Mail, User } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';

export default function RegisterPropertyManagerPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided. Please use the link from your invitation email.');
      setLoading(false);
      return;
    }
    validateToken();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  const validateToken = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getManagerInvitation(token);
      setInvitation(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation. Please contact the landlord for a new invitation.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    try {
      setSubmitting(true);
      await authAPI.register({
        email: invitation.email,
        password: password,
        name: invitation.name,
        role: 'property_manager',
        invitation_token: token,
      });
      toast.success('Account created! You can now log in.');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F0E8] to-[#E8E0D5] dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#C85A42]" />
          <p className="text-muted-foreground">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F0E8] to-[#E8E0D5] dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
            <h3 className="text-lg font-medium mb-2">Invalid Invitation</h3>
            <p className="text-muted-foreground text-center max-w-sm mb-6">{error}</p>
            <Link to="/login">
              <Button variant="outline">Go to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F0E8] to-[#E8E0D5] dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="manager-register-form">
        <CardHeader className="text-center">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>
          <div className="w-14 h-14 rounded-xl bg-[#C85A42]/10 flex items-center justify-center mx-auto mb-4">
            <UserCog className="w-7 h-7 text-[#C85A42]" />
          </div>
          <CardTitle className="text-2xl">Accept Invitation</CardTitle>
          <CardDescription>
            Set your password to complete registration
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Invitation Details */}
          <div className="bg-muted/50 border rounded-lg p-4 mb-6 space-y-3">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-[#C85A42] shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Invited by</p>
                <p className="font-medium">{invitation.landlord_name}{invitation.company_name ? ` (${invitation.company_name})` : ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Your name</p>
                <p className="font-medium">{invitation.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Your email</p>
                <p className="font-medium">{invitation.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Badge variant="outline" className="text-xs">
                <Shield className="w-3 h-3 mr-1" />
                {invitation.permissions_count} Permissions
              </Badge>
              <Badge variant="outline" className="text-xs">
                <Building2 className="w-3 h-3 mr-1" />
                {invitation.all_properties ? 'All Properties' : 'Selected Properties'}
              </Badge>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Create Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="manager-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="manager-confirm-password-input"
                />
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="terms-agreement"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                data-testid="terms-agreement-checkbox"
              />
              <label htmlFor="terms-agreement" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                I agree to the <Link to="/terms-of-service" target="_blank" className="text-[#C85A42] hover:underline font-medium">Terms of Service</Link> and <Link to="/privacy-policy" target="_blank" className="text-[#C85A42] hover:underline font-medium">Privacy Policy</Link>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#C85A42] hover:bg-[#A84832]"
              disabled={submitting || !agreedToTerms}
              data-testid="manager-register-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account & Accept'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-4">
            Already have an account?{' '}
            <Link to="/login" className="text-[#C85A42] hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
